import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Rocket, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface PublishVersionDialogProps {
  currentVersion: string;
  standardId: string | null;
  onPublish: (newVersion: string, changelog: string) => Promise<void>;
  disabled?: boolean;
}

type BumpType = 'major' | 'minor' | 'patch';

function bumpVersion(current: string, type: BumpType): string {
  const parts = current.split('.').map(Number);
  const [major = 1, minor = 0, patch = 0] = parts;
  
  switch (type) {
    case 'major':
      return `${major + 1}.0.0`;
    case 'minor':
      return `${major}.${minor + 1}.0`;
    case 'patch':
      return `${major}.${minor}.${patch + 1}`;
    default:
      return current;
  }
}

export function PublishVersionDialog({ 
  currentVersion, 
  standardId, 
  onPublish, 
  disabled 
}: PublishVersionDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [bumpType, setBumpType] = useState<BumpType>('patch');
  const [changelog, setChangelog] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);

  const newVersion = bumpVersion(currentVersion, bumpType);

  const handlePublish = async () => {
    if (changelog.length < 10) {
      toast({ 
        title: 'Changelog required', 
        description: 'Please provide at least 10 characters describing what changed.',
        variant: 'destructive' 
      });
      return;
    }

    setIsPublishing(true);
    try {
      await onPublish(newVersion, changelog);
      setOpen(false);
      setChangelog('');
      setBumpType('patch');
    } catch (error) {
      console.error('Publish error:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const bumpOptions: { type: BumpType; label: string; description: string }[] = [
    { type: 'patch', label: 'Patch', description: 'Bug fixes, documentation' },
    { type: 'minor', label: 'Minor', description: 'New fields, backwards compatible' },
    { type: 'major', label: 'Major', description: 'Breaking changes' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled || !standardId}>
          <Rocket className="h-4 w-4 mr-2" />
          Publish New Version
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Publish New Version</DialogTitle>
          <DialogDescription>
            Create a snapshot of the current schema state. Published versions are immutable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Version Preview */}
          <div className="flex items-center justify-center gap-4 py-3 bg-muted rounded-lg">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Current</p>
              <Badge variant="secondary" className="text-lg font-mono">
                v{currentVersion}
              </Badge>
            </div>
            <span className="text-2xl text-muted-foreground">→</span>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">New</p>
              <Badge className="text-lg font-mono">
                v{newVersion}
              </Badge>
            </div>
          </div>

          {/* Bump Type Selection */}
          <div className="space-y-2">
            <Label>Version Bump</Label>
            <div className="grid grid-cols-3 gap-2">
              {bumpOptions.map(({ type, label, description }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setBumpType(type)}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    bumpType === type
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/50'
                  }`}
                >
                  <p className="font-medium text-sm">{label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Major Version Warning */}
          {bumpType === 'major' && (
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-600">
                Major version bumps indicate breaking changes. Make sure consumers 
                of this schema are aware of the changes.
              </p>
            </div>
          )}

          {/* Changelog */}
          <div className="space-y-2">
            <Label htmlFor="changelog">Changelog *</Label>
            <Textarea
              id="changelog"
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              placeholder="Describe what changed in this version..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              {changelog.length}/10 characters minimum
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handlePublish} 
            disabled={isPublishing || changelog.length < 10}
          >
            {isPublishing ? 'Publishing...' : `Publish v${newVersion}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
